import { FileService } from '../../services/fileApiService';
import { FunctionService } from '../../services/functionService';
import { decryptBlob } from '../blobDecryption';

const fileService = new FileService();
const functionService = new FunctionService();

const CACHE_MAX_ENTRIES = 15;
const cache = new Map<number, string>();
const cacheOrder: number[] = [];
const inFlight = new Map<number, Promise<string>>();

/**
 * In-memory LRU cache of full-resolution preview blob URLs (session-only).
 * ponytail: upgrade path = Dexie `filePreviewBlobs` + TTL similar to `fileThumbnails`.
 */

/** Maps a file extension to the MIME type used when decrypting an image preview. */
function mimeFromImageExtension(fileExtension: string): string {
  const normalized = fileExtension.toLowerCase().replace(/^\./, '');
  if (normalized === 'jpg' || normalized === 'jpeg') return 'image/jpeg';
  if (normalized === 'png') return 'image/png';
  if (normalized === 'svg') return 'image/svg+xml';
  return 'image/jpeg';
}

/** Updates LRU order after a cache hit or insert. */
function touchCacheOrder(fileId: number): void {
  const index = cacheOrder.indexOf(fileId);
  if (index >= 0) cacheOrder.splice(index, 1);
  cacheOrder.push(fileId);
}

/** Drops oldest entries and revokes their object URLs once {@link CACHE_MAX_ENTRIES} is exceeded. */
function evictOldestCacheEntry(): void {
  while (cacheOrder.length > CACHE_MAX_ENTRIES) {
    const evictedFileId = cacheOrder.shift();
    if (evictedFileId === undefined) break;
    const blobUrl = cache.get(evictedFileId);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    cache.delete(evictedFileId);
  }
}

/** Stores a blob URL and enforces the LRU size limit. */
function setCacheEntry(fileId: number, blobUrl: string): void {
  const existingUrl = cache.get(fileId);
  if (existingUrl) URL.revokeObjectURL(existingUrl);
  cache.set(fileId, blobUrl);
  touchCacheOrder(fileId);
  evictOldestCacheEntry();
}

/** @returns Whether a full-resolution preview URL is already cached for `fileId`. */
export function hasFullPreviewCached(fileId: number): boolean {
  return cache.has(fileId);
}

/** @returns The cached full-resolution blob URL for `fileId`, if any. */
export function getCachedFullPreviewUrl(fileId: number): string | undefined {
  const cachedUrl = cache.get(fileId);
  if (!cachedUrl) return undefined;
  touchCacheOrder(fileId);
  return cachedUrl;
}

/** Progress payload forwarded from {@link FunctionService.getFileBlob}. */
export interface FullPreviewDownloadProgress {
  percent?: number;
  receivedBytes: number;
  totalBytes?: number;
}

/**
 * Fetches, decrypts, and caches a full-resolution preview blob URL for a file.
 * Concurrent requests for the same `fileId` share one in-flight promise.
 *
 * @param fileId File to preview.
 * @param fileSize Declared file size passed to the blob download API.
 * @param fileExtension Used to pick the decrypted image MIME type.
 * @param onProgress Optional download progress callback.
 * @returns Object URL for the decrypted preview image.
 * @throws When the viewer token cannot be obtained or the download/decrypt pipeline fails.
 */
export async function fetchFileFullPreviewUrl(
  fileId: number,
  fileSize: number,
  fileExtension: string,
  onProgress?: (progress: FullPreviewDownloadProgress) => void,
): Promise<string> {
  const cachedUrl = cache.get(fileId);
  if (cachedUrl) {
    touchCacheOrder(fileId);
    return cachedUrl;
  }

  const pendingFetch = inFlight.get(fileId);
  if (pendingFetch) return pendingFetch;

  const fetchPromise = (async () => {
    const tokenResponse = await fileService.getFileForViewer(fileId, {});
    if (!tokenResponse.token) {
      throw new Error('Failed to get file preview token');
    }

    const encryptedBlob = await functionService.getFileBlob(
      tokenResponse.token,
      fileSize,
      onProgress,
    );
    const { decrypted } = await decryptBlob(encryptedBlob, mimeFromImageExtension(fileExtension));
    const blobUrl = URL.createObjectURL(decrypted);
    setCacheEntry(fileId, blobUrl);
    return blobUrl;
  })();

  inFlight.set(fileId, fetchPromise);

  try {
    return await fetchPromise;
  } finally {
    inFlight.delete(fileId);
  }
}

/** Clears the module cache; for tests only. */
export function clearFullPreviewCacheForTests(): void {
  for (const blobUrl of cache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  cache.clear();
  cacheOrder.length = 0;
  inFlight.clear();
}
